import { IconUpload } from '@maia/icons';
import { FormField, getErrors, Typography, UploadedFile } from '@maia/ui';
import { ComponentProps } from 'react';
import {
  Button as AriaButton,
  DropZone as AriaDropZone,
  FileTrigger as AriaFileTrigger,
} from 'react-aria-components';
import { useFieldContext } from '../dynamic-form';
import { DocumentDto } from '@maia/shared';

type UploadAttachmentsProps = Omit<
  ComponentProps<typeof FormField>,
  'inputSlot'
>;

type FileData = {
  filename: string;
  encoded_document: string;
  mimetype: string;
};

export function UploadAttachments(props: UploadAttachmentsProps) {
  const { ...rest } = props;
  const field = useFieldContext<object>();

  return (
    <FormField
      {...rest}
      wrapperField="div"
      error={getErrors(field)}
      inputSlot={
        <>
          <AriaDropZone
            className="bg-grayscale50 border border-grayscale200 rounded-12 flex justify-center max-w-[344px]"
            onDrop={(e) =>
              handleDroppedFiles(e.items).then((e) => field.handleChange(e[0]))
            }
          >
            <AriaFileTrigger
              onSelect={(e) =>
                handleSelectedFiles(e).then((fileData) =>
                  field.handleChange(fileData[0]),
                )
              }
            >
              <AriaButton
                className="flex flex-col items-center w-full p-24"
                id="123"
              >
                <IconUpload className="size-32 fill-grayscale300 mb-4" />
                <Typography
                  variant="body-s"
                  weight="regular"
                  className="text-grayscale300"
                >
                  Sleep bestand of klik
                </Typography>
              </AriaButton>
            </AriaFileTrigger>
          </AriaDropZone>
          {field.state.value && field.state.value instanceof DocumentDto && (
            <ul className="flex gap-12 mt-24">
              <li>
                <UploadedFile
                  mimetype={field.state.value.mimetype}
                  name={field.state.value.filename}
                  className="w-fit"
                  action="delete"
                  onDelete={() => field.handleChange(() => null)}
                />
              </li>
            </ul>
          )}
        </>
      }
    />
  );
}

// Helper function to convert a file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fullBase64 = reader.result?.toString() ?? '';
      const base64Data = fullBase64.substring(fullBase64.indexOf(',') + 1);
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Handle files dropped into the drop zone
async function handleDroppedFiles(
  items: readonly DataTransferItem[],
): Promise<FileData[]> {
  const fileItems = items?.filter((item) => item.kind === 'file');

  return Promise.all(
    fileItems.map(async (item) => {
      const file = await item.getFile();
      const base64 = await fileToBase64(file);
      return {
        filename: file.name,
        mimetype: file.type,
        encoded_document: base64,
      };
    }),
  );
}

// Handle files selected from file dialog
async function handleSelectedFiles(
  files: FileList | null,
): Promise<FileData[]> {
  if (!files) return [];

  return Promise.all(
    Array.from(files).map(async (file) => {
      const base64 = await fileToBase64(file);

      return {
        filename: file.name,
        mimetype: file.type,
        encoded_document: base64,
      };
    }),
  );
}
